import { useState } from 'react'

import { useTranslations } from 'next-intl'

import classNames from 'classnames'

import type { ArtworkCardProps as artworkCard } from '~/components/atoms/ArtworkCard/ArtworkCard'
import Button, {
  ButtonTypes,
  ButtonVariants,
} from '~/components/atoms/Button/Button'
import Image from '~/components/atoms/Image/Image'
import ImagePlaceholder from '~/components/atoms/ImagePlaceholder/ImagePlaceholder'
import { ExtendedTableContent } from '~/components/atoms/Table/Table'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import TicketLozenges, {
  TicketLozengeSizes,
} from '~/components/atoms/TicketLozenges/TicketLozenges'
import { salesValuationTypes } from '~/enums/hsSalesValuationStatus'
import { valuationStatusTypes } from '~/enums/hsValuationStatus'
import type { SaleStatusTypes } from '~/enums/sale-status'
import useGeoLocation from '~/hooks/useLocation'
import useMabPostHog from '~/hooks/useMabPostHog'
import usePortfolioInfoWindow from '~/hooks/usePortfolioInfoWindow'
import { getStringInitials } from '~/utils/getStringInitials'
import { renderGuidePrice, renderMarketValue } from '~/utils/portfolioRenderers'
import {
  PushInteractionLocation,
  PushInteractionType,
  PushWebProduct,
} from '~/utils/posthogEvents'

import EmptyGrowthMessage from '../EmptyGrowthMessage/EmptyGrowthMessage'
import type { growthType } from '../GrowthStatus/GrowthStatus'
import GrowthStatus from '../GrowthStatus/GrowthStatus'
import { PortfolioCollectionVariant } from '../PortfolioCollection/PortfolioCollection'
import PortfolioItemDelete from '../PortfolioCollectionTableMenu/PortfolioItemDelete'

import styles from './PortfolioCard.module.css'

export type PortfolioCardVariant =
  | 'default'
  | 'large'
  | 'largeCard'
  | 'medium'
  | 'small'

interface PortfolioCardHeadProps {
  title: string
  signed: string
  artist?: string
  format?: string
  variant?: PortfolioCardVariant
}

export const PortfolioCardHead = ({
  title,
  signed,
  artist,
  format,
  variant = 'default',
}: PortfolioCardHeadProps) => {
  return (
    <div
      className={classNames(styles.text, {
        [styles.large]: variant === 'large',
        [styles.largeCard]: variant === 'largeCard',
        [styles.medium]: variant === 'medium',
      })}
    >
      {title && (
        <Text
          Tag="p"
          className={styles.title}
          size={TextSizes.secondaryTitleMed}
        >
          {title}
        </Text>
      )}
      {variant === 'large' ? (
        <>
          {artist && (
            <Text
              Tag="p"
              size={TextSizes.primaryTitleSml}
              className={styles.artist}
            >
              {artist}
            </Text>
          )}
          {format && (
            <Text
              Tag="p"
              className={styles.signedItem}
              size={TextSizes.secondaryTitleSml}
            >
              {format}
            </Text>
          )}
        </>
      ) : (
        <>
          {format && (
            <Text
              Tag="p"
              className={styles.signedItem}
              size={TextSizes.secondaryTitleSml}
            >
              {format}
            </Text>
          )}
          {artist && (
            <Text
              Tag="p"
              size={TextSizes.primaryTitleSml}
              className={styles.artist}
            >
              {artist}
            </Text>
          )}
        </>
      )}
      {/* {signed && (
        <Text
          Tag="p"
          className={styles.signedItem}
          size={TextSizes.secondaryTitleSml}
        >
          {signed}
        </Text>
      )} */}
    </div>
  )
}

interface PortfolioCardTitleAndTextProps {
  title: string
  text: string
}

const PortfolioCardTitleAndText = ({
  title,
  text,
}: PortfolioCardTitleAndTextProps) => {
  return (
    <div className={styles.statItem}>
      <Text size={TextSizes.secondaryTitleMicro}>{title}: </Text>

      <Text className={styles.statItemValue} size={TextSizes.primaryTitleXSml}>
        <span dangerouslySetInnerHTML={{ __html: text }} />
      </Text>
    </div>
  )
}

export interface PortfolioCardItemProps {
  artwork: artworkCard
  ticketId: string
  purchasePrice?: number
  growthSinceOwned?: growthType
  marketValue?: JSX.Element
  growth5Year?: growthType
  guidePrice?: JSX.Element
  saleStatus: SaleStatusTypes
  purchaseStatus?: string
  inventoryPriority?: number
  hasHistory: boolean
  salesValuation?: number | string
  auctionData?: any
  marketValuationStatus?: valuationStatusTypes
  salesValutionStatus?: salesValuationTypes
  buyTicketsCount?: number
  sellTicketsCount?: number
  ownedTicketsCount?: number
  insuranceValuationReplacementDate?: string
  insuranceReplacementAmount?: string
  insuranceValuationStatus?: string
  insuranceRemindMeFrequency?: string
  insuranceValuationRequestedDate?: string
  insuranceValuationLastUpdated?: string
}

export interface PortfolioCardProps {
  testID?: string
  item: PortfolioCardItemProps
  offwhite?: boolean
  variant: PortfolioCollectionVariant
}

export default function PortfolioCard({
  item,
  offwhite = false,
  variant,
  testID,
}: PortfolioCardProps) {
  const { pushEvent } = useMabPostHog()
  const tPortfolio = useTranslations('portfolio')
  const { activeCurrencyIso } = useGeoLocation()
  if (!item) return null
  const { image, title, signed, artist, format, imageUrl } = item.artwork
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { openInfoWindow } = usePortfolioInfoWindow()
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [expandedState, setExpandedState] = useState<boolean>(false)

  return (
    <div
      className={classNames(styles.base, offwhite && styles.offwhite)}
      data-testid={testID}
    >
      <div className={styles.headerWrap}>
        <div className={styles.imageCol}>
          <div className={classNames(styles.imageContainer, {})}>
            <div
              className={classNames(styles.imageContainerInner)}
              onClick={() => openInfoWindow(item, variant)}
            >
              {(image && image.asset) || imageUrl ? (
                <Image
                  asset={imageUrl ? imageUrl : image.asset}
                  alt={`${title} - ${format}`}
                  objectFit="contain"
                  sizes="(max-width: 559px) 100vw, (max-width: 1079px) 33vw, 25vw"
                />
              ) : (
                <ImagePlaceholder
                  text={artist ? getStringInitials(artist) : ''}
                />
              )}
            </div>
          </div>
        </div>
        <div
          className={styles.textCol}
          onClick={() => openInfoWindow(item, variant)}
        >
          <PortfolioCardHead
            title={title}
            signed={signed}
            artist={artist}
            format={format}
          />
        </div>
        <div className={styles.btnCol}>
          <Button
            smallFont
            reducedPadding
            button={{
              type: ButtonTypes.button,
              onClick: () => {
                openInfoWindow(item, variant)
                pushEvent('Clicked View Artwork', {
                  webProduct: PushWebProduct.MyPortfoio,
                  interactionType: PushInteractionType.Click,
                  interactionLocation: PushInteractionLocation.MyPortfolioPage,
                })
              },
            }}
          >
            {tPortfolio('view')}
          </Button>
        </div>
      </div>
      <div className={styles.cardBody}>
        <div className={styles.stats}>
          {variant === PortfolioCollectionVariant.Selling && (
            <div className={styles.salesValuationItem}>
              <Text size={TextSizes.secondaryTitleMicro}>
                {tPortfolio('salesValuation')}
              </Text>
              {item.salesValutionStatus ===
              salesValuationTypes.viewValuation ? (
                <Button
                  button={{
                    type: ButtonTypes.button,
                    onClick: () => openInfoWindow(item, variant),
                  }}
                  underline
                  variant={ButtonVariants.textLinkSmall}
                >
                  {tPortfolio('viewValuation')}
                </Button>
              ) : (
                <Text size={TextSizes.primaryTitleXSml}>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: item.salesValuation
                        ? item.salesValuation.toString()
                        : '',
                    }}
                  />
                </Text>
              )}
            </div>
          )}
          {item.guidePrice ? (
            <div className={styles.statItem}>
              <Text size={TextSizes.secondaryTitleMicro}>
                {tPortfolio('guidePrice')}
              </Text>
              {renderGuidePrice(item.guidePrice, activeCurrencyIso || '')}
            </div>
          ) : null}
          {item.purchaseStatus
            ? variant === PortfolioCollectionVariant.Buying && (
                <PortfolioCardTitleAndText
                  title={tPortfolio('purchaseStatus')}
                  text={item.purchaseStatus}
                />
              )
            : null}
          {variant === PortfolioCollectionVariant.Myportfolio && (
            <div className={styles.statItem}>
              <Text size={TextSizes.secondaryTitleMicro}>
                {tPortfolio('marketValue')}
              </Text>
              {item.marketValuationStatus ===
              valuationStatusTypes.viewValuation ? (
                <Button
                  button={{
                    type: ButtonTypes.button,
                    onClick: () => openInfoWindow(item, variant),
                  }}
                  underline
                  variant={ButtonVariants.textLinkSmall}
                >
                  {tPortfolio('viewValuation')}
                </Button>
              ) : (
                <>
                  {renderMarketValue(item.marketValue, activeCurrencyIso || '')}{' '}
                  {item.artwork.agnostic && item.marketValue && (
                    <Text size={TextSizes.bodySml}>
                      {tPortfolio('myEstimation')}
                    </Text>
                  )}
                </>
              )}
            </div>
          )}
          {item.growthSinceOwned ? (
            <div className={styles.statItem}>
              <Text size={TextSizes.secondaryTitleMicro}>
                {tPortfolio('growthSinceOwned')}
              </Text>
              {item.growthSinceOwned && (
                <div
                  className={styles.actionable}
                  onClick={() => openInfoWindow(item, variant)}
                >
                  {' '}
                  <GrowthStatus growth={+item.growthSinceOwned} />
                </div>
              )}
            </div>
          ) : null}

          {item.growth5Year ? (
            <div className={styles.statItem}>
              <Text size={TextSizes.secondaryTitleMicro}>
                {tPortfolio('growth5Years')}
              </Text>
              {item.growth5Year && (
                <div
                  className={styles.actionable}
                  onClick={() => openInfoWindow(item, variant)}
                >
                  {' '}
                  <GrowthStatus growth={+item.growth5Year} />
                </div>
              )}
            </div>
          ) : (
            <div className={styles.statItem}>
              <Text size={TextSizes.secondaryTitleMicro}>
                {tPortfolio('growth5Years')}
              </Text>
              <div
                onClick={() => setExpandedState(!expandedState)}
                className={styles.emptyGrowth}
              >
                <GrowthStatus
                  size="medium"
                  growth={undefined}
                  graphGrey
                  showInfoIcon
                  showEmptyText
                />
              </div>
            </div>
          )}

          {expandedState && (
            <div className={styles.expandedContent}>
              <ExtendedTableContent
                content={<EmptyGrowthMessage />}
                setExpandedState={() => setExpandedState(false)}
              />
            </div>
          )}
          {item.saleStatus && (
            <PortfolioCardTitleAndText
              title="Sale status"
              text={item.saleStatus}
            />
          )}
          <div className={styles.tradingFloorLozenges}>
            <TicketLozenges
              buyTicketsCount={item.artwork.buyTicketsCount}
              sellTicketsCount={item.artwork.sellTicketsCount}
              ownedTicketsCount={item.artwork.ownedTicketsCount}
              size={TicketLozengeSizes.small}
              borderTop
              paddingTop
            />
            {variant !== PortfolioCollectionVariant.Selling && (
              <PortfolioItemDelete item={item} />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
